/**
 * Desk: share / embed / MLS-unbranded links and optional password for a
 * Bricks-hosted Virtual Visit (property_visits row with a splat asset).
 */
import { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { T, F } from "@/lib/bricks";
import { visitEmbedCode, visitPublicUrl } from "@/lib/visitManifest";

function appOrigin() {
  if (typeof window === "undefined") return "https://app.brxs.ca";
  const o = window.location.origin;
  return /localhost|127\.0\.0\.1/.test(o) ? o : "https://app.brxs.ca";
}

async function copy(text, label) {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  } catch {
    toast.error("Copy failed — select and copy manually");
  }
}

export default function VisitEmbedPanel({ visit, p, onChanged }) {
  const [unbranded, setUnbranded] = useState(false);
  const [pw, setPw] = useState("");
  const [saving, setSaving] = useState(false);
  if (!visit || !visit.id || visit.type !== "file") return null;
  const origin = appOrigin();
  const link = visitPublicUrl(origin, visit.id, { unbranded: false });
  const mls = visitPublicUrl(origin, visit.id, { unbranded: true });
  const code = visitEmbedCode(origin, visit.id, { unbranded, title: visit.title || "Virtual Visit" });

  const row = { display: "flex", gap: 8, alignItems: "center" };
  const field = {
    flex: 1, minWidth: 0, padding: "8px 10px", fontSize: 12, fontFamily: F.mono,
    border: `1px solid ${p.hair}`, borderRadius: 2, background: p.surf2, color: p.ink2,
    whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
  };
  const btn = {
    padding: "8px 12px", fontSize: 12, fontFamily: F.display, borderRadius: 2,
    border: `1px solid ${p.hair}`, background: "transparent", color: p.ink, cursor: "pointer", whiteSpace: "nowrap",
  };
  const label = { fontSize: 12, color: p.ink3 };

  async function savePassword(clear) {
    setSaving(true);
    try {
      await api.put(`/visits/${visit.id}/settings`, clear ? { clear_password: true } : { password: pw });
      toast.success(clear ? "Password removed — link is public" : "Password set");
      setPw("");
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save password");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div data-testid="visit-embed-panel" style={{ marginTop: 16, padding: 16, border: `1px solid ${p.hair}`, borderRadius: 2, display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <span style={label}>Visit link</span>
        <div style={row}>
          <div style={field} title={link}>{link}</div>
          <button type="button" style={btn} onClick={() => copy(link, "Link")}>Copy</button>
          <a href={link} target="_blank" rel="noopener noreferrer" style={{ ...btn, textDecoration: "none" }}>Open</a>
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <span style={label}>MLS unbranded link</span>
        <div style={row}>
          <div style={field} title={mls}>{mls}</div>
          <button type="button" style={btn} onClick={() => copy(mls, "Unbranded link")}>Copy</button>
        </div>
        <span style={{ fontSize: 12, color: p.ink3 }}>No agent name, brokerage, contact details or Bricks mark.</span>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <div style={{ ...row, justifyContent: "space-between" }}>
          <span style={label}>Embed code</span>
          <label style={{ fontSize: 12, color: p.ink2, display: "inline-flex", gap: 6, alignItems: "center", cursor: "pointer" }}>
            <input type="checkbox" checked={unbranded} onChange={(e) => setUnbranded(e.target.checked)} /> Unbranded
          </label>
        </div>
        <textarea readOnly value={code} rows={3} onFocus={(e) => e.target.select()} style={{ ...field, whiteSpace: "pre-wrap", resize: "vertical", fontSize: 11 }} />
        <div><button type="button" style={btn} onClick={() => copy(code, "Embed code")}>Copy embed code</button></div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <span style={label}>Password {visit.protected ? "· on" : "· off"}</span>
        <div style={row}>
          <input
            type="password"
            autoComplete="new-password"
            placeholder={visit.protected ? "New password" : "Optional password (4+ characters)"}
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            style={{ ...field, fontFamily: F.display, background: p.surf, color: p.ink }}
          />
          <button type="button" style={{ ...btn, background: pw.length >= 4 ? T.brick : "transparent", color: pw.length >= 4 ? "#fff" : p.ink3, borderColor: pw.length >= 4 ? T.brick : p.hair }} disabled={saving || pw.length < 4} onClick={() => savePassword(false)}>
            {visit.protected ? "Change" : "Set"}
          </button>
          {visit.protected && (
            <button type="button" style={btn} disabled={saving} onClick={() => savePassword(true)}>Remove</button>
          )}
        </div>
        <span style={{ fontSize: 12, color: p.ink3 }}>Protects the link, the embed and the Visit on your client share page.</span>
      </div>
    </div>
  );
}
